/**
 * Édition des overrides de colonnes d'un tableau paramétrable.
 *
 * Le catalogue vient du backend (ordre et largeurs d'origine), les overrides
 * de l'utilisateur (`tableConfig.columns`). Les deux se fusionnent **par
 * libellé** — seul identifiant stable, les clés techniques étant régénérées à
 * chaque lecture.
 *
 * Module partagé document ↔ site : pur, sans React ni store. L'écran de
 * configuration, côté document comme côté site, n'a qu'à rendre le résultat.
 */

/**
 * Catalogue + overrides → la liste affichée, dans l'ordre courant.
 * Une colonne du catalogue sans override garde sa position et sa largeur.
 */
export function mergeColumns(catalog, overrides) {
  const byLabel = new Map((overrides || []).map((c) => [c.label, c]));
  return (catalog || [])
    .map((col, idx) => {
      const o = byLabel.get(col.label) || {};
      return {
        label: col.label,
        // Renommage de présentation ; `label` reste la clé de matching stable.
        customLabel: o.customLabel,
        hidden: !!o.hidden,
        order: o.order ?? idx,
        // Largeur en pourcent ; défaut = largeur du catalogue.
        width: o.width ?? col.width,
        // Alignement (start | center | end) : celui choisi dans le document,
        // sinon celui du catalogue.
        align: o.align ?? col.align,
        alignOverride: o.align,
        verticalAlign: o.verticalAlign,
        // Alignement du seul titre, choisi dans l'en-tête du document.
        headerAlign: o.headerAlign,
        // `image` : l'aperçu y montre une image (logo, planning), pas un montant.
        type: col.type,
      };
    })
    .sort((a, b) => a.order - b.order);
}

/**
 * Liste affichée → overrides à stocker, avec un ordre séquentiel réassigné.
 * On réécrit **toujours** la liste complète : un override partiel laisserait
 * des colonnes sans position et l'ordre deviendrait dépendant du catalogue.
 */
export function toColumnOverrides(columns) {
  return (columns || []).map((c, idx) => ({
    label: c.label,
    ...(c.customLabel != null ? { customLabel: c.customLabel } : {}),
    hidden: c.hidden,
    order: idx,
    width: c.width,
    ...(c.alignOverride ? { align: c.alignOverride } : {}),
    ...(c.verticalAlign ? { verticalAlign: c.verticalAlign } : {}),
    ...(c.headerAlign ? { headerAlign: c.headerAlign } : {}),
  }));
}

const ALIGN_CYCLE = ['start', 'center', 'end'];

/** Alignement suivant d'une colonne : gauche → centre → droite → gauche. */
export function cycleColumnAlign(columns, label) {
  return columns.map((c) => {
    if (c.label !== label) return c;
    const next = ALIGN_CYCLE[(ALIGN_CYCLE.indexOf(c.align ?? 'start') + 1) % ALIGN_CYCLE.length];
    return { ...c, align: next, alignOverride: next };
  });
}

/**
 * Nouvelle largeur d'une colonne, la **dernière colonne visible absorbant
 * l'écart** pour que le total reste à 100 %. C'est pour ça qu'elle n'est pas
 * éditable directement dans l'écran de configuration.
 */
export function setColumnWidth(columns, label, value) {
  const visible = columns.filter((c) => !c.hidden);
  const last = visible[visible.length - 1];
  const updated = columns.map((c) => (c.label === label ? { ...c, width: value } : c));
  if (!last || last.label === label || visible.length < 2) return updated;

  const othersSum = updated
    .filter((c) => !c.hidden && c.label !== last.label)
    .reduce((sum, c) => sum + (c.width || 0), 0);
  const lastWidth = Math.max(1, Math.min(100, 100 - othersSum));
  return updated.map((c) => (c.label === last.label ? { ...c, width: lastWidth } : c));
}

/**
 * Masque / réaffiche une colonne puis **recalcule les largeurs** des colonnes
 * visibles pour qu'elles totalisent 100 % (proportionnellement, la dernière
 * absorbant l'arrondi). Sans ça, le tableau se redimensionne à l'écran mais les
 * pourcentages affichés restent ceux d'avant.
 */
export function toggleColumnHidden(columns, label) {
  const toggled = columns.map((c) => (c.label === label ? { ...c, hidden: !c.hidden } : c));
  const visible = toggled.filter((c) => !c.hidden);
  if (!visible.length) return toggled;

  const total = visible.reduce((sum, c) => sum + (c.width || 0), 0);
  const lastLabel = visible[visible.length - 1].label;
  const scaled = new Map();
  let assigned = 0;
  visible.forEach((c) => {
    if (c.label === lastLabel) return;
    const width = total > 0 ? Math.round(((c.width || 0) / total) * 10000) / 100 : Math.round(10000 / visible.length) / 100;
    scaled.set(c.label, width);
    assigned += width;
  });
  scaled.set(lastLabel, Math.round((100 - assigned) * 100) / 100);
  return toggled.map((c) => (scaled.has(c.label) ? { ...c, width: scaled.get(c.label) } : c));
}

/** Déplace une colonne d'un cran (chevrons). Hors bornes : rien ne bouge. */
export function moveColumn(columns, index, direction) {
  const target = index + direction;
  if (target < 0 || target >= columns.length) return columns;
  const next = [...columns];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/** Insère la colonne tirée à la position survolée (drag). */
export function moveColumnByLabel(columns, draggedLabel, overLabel) {
  const from = columns.findIndex((c) => c.label === draggedLabel);
  const to = columns.findIndex((c) => c.label === overLabel);
  if (from < 0 || to < 0 || from === to) return columns;
  const next = [...columns];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}
